import Layout from "modules/layout/dashboard"

import React, { useRef } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Button, Flex, Space, Typography } from "antd"
import { useTranslation } from "react-i18next"
import PageHeader from "modules/layout/dashboard/main/page-header"
import type { IActivityExpenseForm } from "./types"
import { useUpdateActivityExpense, useActivityExpenseTracking } from "./queries"
import {
  ISession,
  useExtSessions,
} from "pages/assignations/preassignations/queries"
import { setFormData } from "utils/form-data"
import { useActivityExpenseId } from ".."
import ActivityExpenseForm from "./form"
import type { ActivityExpenseFormRef } from "./form"
import Loader from "components/loader"

const { Main, Header } = Layout
const { Title } = Typography

const EditActivityExpense: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const formRef = useRef<ActivityExpenseFormRef>(null)

  const activityExpenseId = useActivityExpenseId()

  const activityExpense = useActivityExpenseTracking(activityExpenseId!, {
    cacheTime: 0,
  })

  const updateActivityExpense = useUpdateActivityExpense(activityExpenseId!)

  const sessions = useExtSessions(undefined, {
    select(data) {
      return searchParams.get("session")
        ? data.filter((session) => session.id === searchParams.get("session"))
        : ([
            data.find((session) => session.status === "progress") ||
              data.find((session) => session.status === "published"),
          ] as ISession[])
    },
    onSuccess(data) {
      if (!searchParams.get("session")) {
        searchParams.set("session", data[0]?.id || "")
        setSearchParams(searchParams)
      }
    },
  })

  const onSubmit = (data: IActivityExpenseForm) => {
    if (typeof data.voucher === "string" || !data.voucher) {
      delete data.voucher
    }
    const formData = setFormData<IActivityExpenseForm>(data)
    formData.append("_method", "PUT")
    updateActivityExpense.mutate(formData, {
      onSuccess() {
        navigate("../..")
      },
      onError(error: any) {
        if (error.response.data?.code === "form/validation") {
          for (let key in error.response.data.errors) {
            formRef.current?.form.setError(key as any, {
              message: error.response.data.errors[key][0].message,
            })
          }
        }
      },
    })
  }

  return (
    <Layout>
      <Header />
      <Main>
        <Flex vertical style={{ margin: "16pt", flexGrow: 1 }} gap="middle">
          <PageHeader
            onButtonClick={() => navigate("../..")}
            title={
              <Title level={4}>
                {t("global.edit_X", {
                  entity: t("finance.expense"),
                  context: "female",
                })}
                {!!sessions.data?.[0].name &&
                  ` (${t("labels.session")}: ${sessions.data?.[0].name})`}
              </Title>
            }
          />
          {activityExpense.isLoading ? (
            <Loader />
          ) : (
            <ActivityExpenseForm
              ref={formRef}
              onSubmit={onSubmit}
              defaultValues={
                activityExpense.data
                  ? {
                      post_id: activityExpense.data.post.id!,
                      activity_id: activityExpense.data.activity.id!,
                      amount: activityExpense.data.amount,
                      date: activityExpense.data.date,
                      voucher: activityExpense.data.voucher,
                      description: activityExpense.data.description,
                    }
                  : undefined
              }
              session={sessions.data?.[0]}
            >
              <>
                <Space>
                  <Button
                    loading={updateActivityExpense.isLoading}
                    disabled={updateActivityExpense.isLoading}
                    type="primary"
                    htmlType="submit"
                  >
                    {t("global.edit")}
                  </Button>
                  <Button
                    onClick={() => navigate("../..")}
                    disabled={updateActivityExpense.isLoading}
                  >
                    {t("global.cancel")}
                  </Button>
                </Space>
              </>
            </ActivityExpenseForm>
          )}
        </Flex>
      </Main>
    </Layout>
  )
}

export default EditActivityExpense
